import React, { useState } from 'react';
import { DynamicPage, PageBlock, PageLayout } from '../../types';
import Button from '../UI/Button';
import Input from '../UI/Input';
import { Plus, Trash2, Image, Type, Video, Save, ArrowUp, ArrowDown, LayoutTemplate, Maximize, AlignCenter, FileText as FileIcon } from 'lucide-react';
import { api } from '../../services/api';

interface PageEditorProps {
  initialData?: DynamicPage;
  onSave: () => void;
  onCancel: () => void;
}

const PageEditor: React.FC<PageEditorProps> = ({ initialData, onSave, onCancel }) => {
  const [title, setTitle] = useState(initialData?.title || '');
  const [slug, setSlug] = useState(initialData?.slug || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [blocks, setBlocks] = useState<PageBlock[]>(initialData?.blocks || []);
  const [layout, setLayout] = useState<PageLayout>(initialData?.layout || 'default');
  const [loading, setLoading] = useState(false);

  const addBlock = (type: PageBlock['type']) => {
    const newBlock: PageBlock = {
      id: Math.random().toString(36).substr(2, 9),
      type,
      content: '',
      meta: ''
    };
    setBlocks([...blocks, newBlock]);
  };

  const updateBlock = (id: string, field: 'content' | 'meta', value: string) => {
    setBlocks(blocks.map(b => b.id === id ? { ...b, [field]: value } : b));
  };

  const removeBlock = (id: string) => {
    setBlocks(blocks.filter(b => b.id !== id));
  };

  const moveBlock = (index: number, direction: 'up' | 'down') => {
    const newBlocks = [...blocks];
    if (direction === 'up' && index > 0) {
      [newBlocks[index], newBlocks[index - 1]] = [newBlocks[index - 1], newBlocks[index]];
    } else if (direction === 'down' && index < newBlocks.length - 1) {
      [newBlocks[index], newBlocks[index + 1]] = [newBlocks[index + 1], newBlocks[index]];
    }
    setBlocks(newBlocks);
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>, blockId: string) => {
    if (e.target.files && e.target.files[0]) {
       const file = e.target.files[0];
       const localUrl = URL.createObjectURL(file);
       updateBlock(blockId, 'content', localUrl);
    }
  };

  const handleSave = async () => {
    if (!title || !slug) return alert('عنوان و آدرس صفحه الزامی است.');
    
    setLoading(true);
    const pageData: DynamicPage = {
      id: initialData?.id || Math.random().toString(36).substr(2, 9),
      title,
      slug: slug.replace(/\s+/g, '-').toLowerCase(),
      description,
      blocks,
      layout,
      createdAt: initialData?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    await api.savePage(pageData);
    setLoading(false);
    onSave();
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 animate-fadeIn">
      <div className="flex justify-between items-center mb-6 pb-4 border-b">
        <h3 className="text-xl font-bold">{initialData ? 'ویرایش صفحه' : 'ساخت صفحه جدید'}</h3>
        <Button size="sm" variant="ghost" onClick={onCancel}>انصراف</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Input 
          label="عنوان صفحه" 
          value={title} 
          onChange={(e) => setTitle(e.target.value)} 
          placeholder="مثلا: مشاوره حقوقی"
        />
        <Input 
          label="آدرس صفحه (Slug)" 
          value={slug} 
          onChange={(e) => setSlug(e.target.value)} 
          placeholder="مثلا: legal-advice"
          dir="ltr"
          className="text-left"
        />
        <div className="md:col-span-2">
           <label className="block text-sm font-medium text-gray-700 mb-1.5">توضیحات کوتاه (متا)</label>
           <input 
              className="w-full border border-gray-600 bg-gray-700 rounded-xl p-2.5 focus:ring-teal-500 text-white placeholder-gray-400"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="توضیحاتی برای نمایش در لیست‌ها و گوگل"
           />
        </div>
        
        {/* Layout Selector */}
        <div className="md:col-span-2">
           <label className="block text-sm font-medium text-gray-700 mb-2">قالب و چیدمان صفحه</label>
           <div className="grid grid-cols-3 gap-4">
              <div 
                onClick={() => setLayout('default')}
                className={`border rounded-xl p-4 cursor-pointer flex flex-col items-center gap-2 transition-all ${layout === 'default' || !layout ? 'border-teal-500 bg-teal-50 ring-2 ring-teal-200' : 'border-gray-200 hover:border-gray-300'}`}
              >
                 <LayoutTemplate className={layout === 'default' ? 'text-teal-600' : 'text-gray-400'}/>
                 <span className={`text-sm font-bold ${layout === 'default' ? 'text-teal-800' : 'text-gray-600'}`}>استاندارد</span>
              </div>
              
              <div 
                onClick={() => setLayout('article')}
                className={`border rounded-xl p-4 cursor-pointer flex flex-col items-center gap-2 transition-all ${layout === 'article' ? 'border-teal-500 bg-teal-50 ring-2 ring-teal-200' : 'border-gray-200 hover:border-gray-300'}`}
              >
                 <FileIcon className={layout === 'article' ? 'text-teal-600' : 'text-gray-400'}/>
                 <span className={`text-sm font-bold ${layout === 'article' ? 'text-teal-800' : 'text-gray-600'}`}>مقاله (متمرکز)</span>
              </div>

              <div 
                onClick={() => setLayout('full')}
                className={`border rounded-xl p-4 cursor-pointer flex flex-col items-center gap-2 transition-all ${layout === 'full' ? 'border-teal-500 bg-teal-50 ring-2 ring-teal-200' : 'border-gray-200 hover:border-gray-300'}`}
              >
                 <Maximize className={layout === 'full' ? 'text-teal-600' : 'text-gray-400'}/>
                 <span className={`text-sm font-bold ${layout === 'full' ? 'text-teal-800' : 'text-gray-600'}`}>تمام عرض</span>
              </div>
           </div>
        </div>
      </div>

      <div className="space-y-4 mb-8">
        <h4 className="font-bold text-gray-800 mb-2">محتوای صفحه</h4>
        {blocks.length === 0 && (
          <div className="text-center p-8 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200 text-gray-400">
            هنوز محتوایی اضافه نکرده‌اید.
          </div>
        )}
        
        {blocks.map((block, index) => (
          <div key={block.id} className="bg-gray-50 p-4 rounded-xl border border-gray-200 group relative">
             <div className="absolute left-2 top-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                <button onClick={() => moveBlock(index, 'up')} disabled={index === 0} className="p-1 bg-white rounded border hover:bg-gray-100 disabled:opacity-30"><ArrowUp size={14}/></button>
                <button onClick={() => moveBlock(index, 'down')} disabled={index === blocks.length - 1} className="p-1 bg-white rounded border hover:bg-gray-100 disabled:opacity-30"><ArrowDown size={14}/></button>
                <button onClick={() => removeBlock(block.id)} className="p-1 bg-white text-red-500 rounded border hover:bg-red-50"><Trash2 size={14}/></button>
             </div>

             <div className="mb-2 text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1">
                {block.type === 'header' && <Type size={14}/>}
                {block.type === 'text' && <Type size={14}/>}
                {block.type === 'image' && <Image size={14}/>}
                {block.type === 'video' && <Video size={14}/>}
                
                {block.type === 'header' && 'سرتیتر'}
                {block.type === 'text' && 'پاراگراف متن'}
                {block.type === 'image' && 'تصویر'}
                {block.type === 'video' && 'ویدیو'}
             </div>

             {block.type === 'header' && (
               <input 
                 className="w-full bg-transparent text-xl font-bold border-b border-gray-300 focus:border-teal-500 outline-none pb-2 text-gray-900"
                 placeholder="عنوان بخش را وارد کنید..."
                 value={block.content}
                 onChange={(e) => updateBlock(block.id, 'content', e.target.value)}
               />
             )}

             {block.type === 'text' && (
               <textarea 
                 className="w-full bg-white border border-gray-300 rounded-lg p-3 focus:ring-teal-500 min-h-[100px] text-gray-900"
                 placeholder="متن خود را اینجا بنویسید..."
                 value={block.content}
                 onChange={(e) => updateBlock(block.id, 'content', e.target.value)}
               />
             )}

             {block.type === 'image' && (
               <div className="flex gap-4 items-start">
                  <div className="w-32 h-32 bg-gray-200 rounded-lg overflow-hidden flex items-center justify-center shrink-0 border border-gray-300">
                     {block.content ? (
                       <img src={block.content} className="w-full h-full object-cover"/>
                     ) : (
                       <Image className="text-gray-400"/>
                     )}
                  </div>
                  <div className="flex-1 space-y-2">
                     <input 
                       type="file" 
                       accept="image/*"
                       className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100"
                       onChange={(e) => handleImageUpload(e, block.id)}
                     />
                     <Input 
                        placeholder="لینک مستقیم تصویر (اختیاری)"
                        value={block.content}
                        onChange={(e) => updateBlock(block.id, 'content', e.target.value)}
                        className="text-left text-gray-900"
                        dir="ltr"
                     />
                     <input 
                       className="w-full border border-gray-300 rounded-lg p-2 text-sm text-gray-900"
                       placeholder="توضیح تصویر (Alt Text)"
                       value={block.meta || ''}
                       onChange={(e) => updateBlock(block.id, 'meta', e.target.value)}
                     />
                  </div>
               </div>
             )}

             {block.type === 'video' && (
               <div className="space-y-3">
                 <Input 
                    label="لینک ویدیو (آپارات، یوتیوب یا لینک مستقیم)"
                    value={block.content}
                    onChange={(e) => updateBlock(block.id, 'content', e.target.value)}
                    placeholder="https://..."
                    dir="ltr"
                    className="text-left text-gray-900"
                 />
                 <div className="bg-yellow-50 p-2 rounded text-xs text-yellow-700 border border-yellow-100">
                    برای نمایش صحیح، لطفا از لینک Embed یا لینک مستقیم فایل mp4 استفاده کنید.
                 </div>
               </div>
             )}
          </div>
        ))}

        <div className="flex gap-2 justify-center py-4 border-t border-dashed border-gray-300">
           <Button size="sm" variant="ghost" onClick={() => addBlock('header')} className="bg-gray-50 border hover:bg-gray-100 text-gray-600"><Plus size={16}/> سرتیتر</Button>
           <Button size="sm" variant="ghost" onClick={() => addBlock('text')} className="bg-gray-50 border hover:bg-gray-100 text-gray-600"><Plus size={16}/> متن</Button>
           <Button size="sm" variant="ghost" onClick={() => addBlock('image')} className="bg-gray-50 border hover:bg-gray-100 text-gray-600"><Plus size={16}/> تصویر</Button>
           <Button size="sm" variant="ghost" onClick={() => addBlock('video')} className="bg-gray-50 border hover:bg-gray-100 text-gray-600"><Plus size={16}/> ویدیو</Button>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-6 border-t">
         <Button variant="ghost" onClick={onCancel}>انصراف</Button>
         <Button onClick={handleSave} disabled={loading} className="w-32">
            {loading ? 'در حال ذخیره...' : 'ذخیره صفحه'}
         </Button>
      </div>
    </div>
  );
};

export default PageEditor;